import { ofType, StateObservable } from 'redux-observable';
import { ajax } from 'rxjs/ajax';
import {  map, catchError, switchMap, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { APP_CONFIG } from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import { Notification } from "../../models/users/Notification";
import { RouteMethods } from '../../config/RouteConfig';
import {AppState} from "../../reducers/RootReducer";
/**
 * typescript declarations
 */
interface FetchUnreadNotificationsCountAction {
    type: typeof FETCH_UNREAD_NOTIFICATIONS_COUNT
}
interface FetchUnreadNotificationsCountSuccessAction {
    type: typeof FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS
    numberOfUnreadNotifications: number
}
interface FetchUnreadNotificationsCountErrorAction {
    type: typeof FETCH_UNREAD_NOTIFICATIONS_COUNT_ERROR
    error: Error
}
type FetchUnreadNotificationsCountActionTypes = FetchUnreadNotificationsCountAction | FetchUnreadNotificationsCountErrorAction | FetchUnreadNotificationsCountSuccessAction

export type UserNotificationActionTypes = FetchUnreadNotificationsActionTypes | FetchUnreadNotificationsCountActionTypes | MarkUserNotificationAsReadActionTypes

/**
 * fetch the total number of unread notifications action types
 * @type {string}
 */
export const FETCH_UNREAD_NOTIFICATIONS_COUNT = "FETCH_UNREAD_NOTIFICATIONS_COUNT";
export const FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS = "FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS";
export const FETCH_UNREAD_NOTIFICATIONS_COUNT_ERROR = "FETCH_UNREAD_NOTIFICATIONS_COUNT_ERROR";

/**
 * initiate fetching the unread notifications from the server
 * three redux action functions to represent the different stages of a request
 */
export const fetchUnreadNotificationsCount = (): FetchUnreadNotificationsCountAction => ( { type: FETCH_UNREAD_NOTIFICATIONS_COUNT });
export const fetchUnreadNotificationsCountSuccess = ( numberOfUnreadNotifications: number ): FetchUnreadNotificationsCountSuccessAction => ({ type: FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS, numberOfUnreadNotifications });
export const fetchUnreadNotificationsCountError = ( error: Error ): FetchUnreadNotificationsCountErrorAction  => ({ type: FETCH_UNREAD_NOTIFICATIONS_COUNT_ERROR, error });

/**
 * epic to actually do the fetching of the number of unread notifications
 */
export function fetchUnreadNotificationsCountEpic(
    action$: Observable< FetchUnreadNotificationsCountActionTypes >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType(FETCH_UNREAD_NOTIFICATIONS_COUNT),

        switchMap(() => {
            return ajax(
                APP_CONFIG.getRequestObject(APP_CONFIG.baseWeb + "user/notifications/count/")
            ).pipe(
                map(response => fetchUnreadNotificationsCountSuccess( response.response as number )),
                catchError(error => {
                    return of(
                        fetchUnreadNotificationsCountError(error),
                        eventNotificationError(
                            error,
                            store$,
                            'Notification',
                            "fetchUnreadNotificationCount"
                        )
                    )
                })
            )
        })
    );
}

/**
 * typescript declarations
 */
interface FetchUnreadNotificationsAction {
    type: typeof FETCH_USER_NOTIFICATIONS_UNREAD
}
interface FetchUnreadNotificationsSuccessAction {
    type: typeof FETCH_USER_NOTIFICATIONS_UNREAD_SUCCESS
    unreadNotifys: Notification[]
}
interface FetchUnreadNotificationsErrorAction {
    type: typeof FETCH_USER_NOTIFICATIONS_UNREAD_ERROR
    error: Error
}
type FetchUnreadNotificationsActionTypes = FetchUnreadNotificationsAction | FetchUnreadNotificationsErrorAction | FetchUnreadNotificationsSuccessAction


/**
 * fetch all unread notifications
 */
export const FETCH_USER_NOTIFICATIONS_UNREAD = "FETCH_USER_NOTIFICATIONS_UNREAD";
export const FETCH_USER_NOTIFICATIONS_UNREAD_SUCCESS = "FETCH_USER_NOTIFICATIONS_UNREAD_SUCCESS";
export const FETCH_USER_NOTIFICATIONS_UNREAD_ERROR = "FETCH_USER_NOTIFICATIONS_UNREAD_ERROR";

export const fetchUserNotificationUnread = (): FetchUnreadNotificationsAction => ({ type: FETCH_USER_NOTIFICATIONS_UNREAD });
export const fetchUserNotificationUnreadSuccess = (unreadNotifys: Notification[] ): FetchUnreadNotificationsSuccessAction => ({ type: FETCH_USER_NOTIFICATIONS_UNREAD_SUCCESS, unreadNotifys });
export const fetchUserNotificationUnreadError = ( error: Error ): FetchUnreadNotificationsErrorAction => ({ type: FETCH_USER_NOTIFICATIONS_UNREAD_ERROR, error });

export function fetchUserNotificationUnreadEpic(
    action$: Observable< FetchUnreadNotificationsActionTypes >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType(FETCH_USER_NOTIFICATIONS_UNREAD),
        switchMap(() => {
            return ajax(APP_CONFIG.getRequestObject(APP_CONFIG.baseWeb + "user/notifications/unread/")).pipe(
                map(response => fetchUserNotificationUnreadSuccess( response.response as Notification[] )),
                catchError(error =>
                    of(
                        fetchUserNotificationUnreadError(error),
                        eventNotificationError(
                            error,
                            store$,
                            "Notification",
                            "fetchUserNotificationUnread"
                        )
                    )
                )
            )
        })
    );
}

/**
 * typescript declarations
 */
interface MarkUserNotificationAsReadAction {
    type: typeof MARK_USER_NOTIFICATION_AS_READ
    id: number
}
interface MarkUserNotificationAsReadSuccessAction {
    type: typeof MARK_USER_NOTIFICATION_AS_READ_SUCCESS
    id: number
}
interface MarkUserNotificationAsReadErrorAction {
    type: typeof MARK_USER_NOTIFICATION_AS_READ_ERROR
    error: Error
}
type MarkUserNotificationAsReadActionTypes = MarkUserNotificationAsReadAction | MarkUserNotificationAsReadErrorAction | MarkUserNotificationAsReadSuccessAction

/**
 * MARK NOTIFICATION AS READ
 */
export const MARK_USER_NOTIFICATION_AS_READ = "MARK_USER_NOTIFICATION_AS_READ";
export const MARK_USER_NOTIFICATION_AS_READ_SUCCESS = "MARK_USER_NOTIFICATION_AS_READ_SUCCESS";
export const MARK_USER_NOTIFICATION_AS_READ_ERROR = "MARK_USER_NOTIFICATION_AS_READ_ERROR";

export const markUserNotificationAsRead = ( id: number ): MarkUserNotificationAsReadAction => ({ type: MARK_USER_NOTIFICATION_AS_READ, id });
export const markUserNotificationAsReadSuccess = ( id: number): MarkUserNotificationAsReadSuccessAction => ({ type: MARK_USER_NOTIFICATION_AS_READ_SUCCESS, id });
export const markUserNotificationAsReadError = ( error: Error): MarkUserNotificationAsReadErrorAction => ({ type: MARK_USER_NOTIFICATION_AS_READ_ERROR, error });

export function markUserNotificationAsReadEpic(
    action$: Observable< MarkUserNotificationAsReadAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(MARK_USER_NOTIFICATION_AS_READ),
        mergeMap(action => {
            const path = APP_CONFIG.baseWeb + "user/notification/" + action.id + "/read/";

            return ajax(APP_CONFIG.getRequestObject(path, RouteMethods.patch)).pipe(
                mergeMap(() => of(
                        markUserNotificationAsReadSuccess(action.id),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.ajaxSuccess
                        )
                    )
                ),
                catchError(error =>
                    of(
                        markUserNotificationAsReadError(error),
                        eventNotificationError(
                            error,
                            store$,
                            'main',
                            'markUserNotificationAsRead'
                        )
                    )
                )
            )
        })
    );
}